import React, { useState, useEffect } from "react";
import { generateBlankReport } from "../../utils/blanksexport";
import { API_BASE_URL } from "../Api/apiconnector";

export default function BlankForm({ currentUser, generateReport }) {
  const [formData, setFormData] = useState({
    name: "",
    patientId: "",
    age: "",
    gender: "",
    testDate: "",
    reportDate: "",
    referralDoctor: "",
    reportTime: "", 
  });

  const [showForm, setShowForm] = useState(true);

  // ✅ Fetch demographics on load
  useEffect(() => {
    const fetchDemographics = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const patientId = urlParams.get("id");
        if (!patientId) return;

        const response = await fetch(`${API_BASE_URL}/fetch-dicom/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ id: patientId }),
        });

        if (!response.ok)
          throw new Error(`Failed to fetch demographics (${response.status})`);

        const data = await response.json();
        console.log("Fetched demographics:", data);

        // ✅ Use study_date directly (already in dd-mm-yyyy)
        const formattedStudyDate = data.study_date || "";

        // ✅ Current date and time in proper format
        const now = new Date();
        const formattedReportDate = formatToDDMMYYYY(now);
        const formattedReportTime = now.toLocaleTimeString("en-GB"); // HH:mm:ss

        setFormData((prev) => ({
          ...prev,
          name: data.patient_name || "",
          patientId: data.patient_id || "",
          age: data.age || "",
          gender: data.gender || "",
          testDate: formattedStudyDate,
          reportDate: formattedReportDate,
          referralDoctor: data.referring_doctor_name || data.referring_doctor || "",
          reportTime: formattedReportTime,
        }));
      } catch (error) {
        console.error("Error fetching DICOM demographics:", error);
      }
    };

    fetchDemographics();
  }, []);

  const formatToDDMMYYYY = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatToISODate = (ddmmyyyy) => {
    if (!ddmmyyyy) return "";
    const [day, month, year] = ddmmyyyy.split("-");
    return `${year}-${month}-${day}`;
  };

  const formatToDDMMYYYYFromISO = (isoDate) => {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-");
    return `${day}-${month}-${year}`;
  };

  const handleInputChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  // ✅ Hide form after submit/back
  if (!showForm) return null;

  // --- Button handlers ---
  const handleBack = () => setShowForm(false);
  const handleReject = () => alert("Report rejected!");
  const handleDone = () => {
    console.log("Submitted data:", formData);

    // ✅ Convert to report format expected by the generator
    const mappedData = {
      NameTextFR2: formData.name,
      IDTextFR2: formData.patientId,
      AgeTextFR2: formData.age,
      GenderTextFR2: formData.gender,
      TestDateTextFR2: formData.testDate,
      ReportDateTextFR2: formData.reportDate,
      ReferralDoctorTextFR2: formData.referralDoctor,
      ReportTimeTextFR2: formData.reportTime,
      // you can add extra fields later (e.g., description, notes, etc.)
    };

    // ✅ Generate formatted report HTML
    const reportHtml = generateBlankReport(mappedData, currentUser);

    if (generateReport) generateReport(reportHtml);

    setShowForm(false);
  };

  // --- JSX UI ---
  return (
    <div className="max-w-3xl mx-auto bg-white rounded-md shadow border p-4">
      {/* --- Header Buttons --- */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-semibold text-gray-700">Blank Report Form</h2>
        <div className="flex gap-2">
          <button
            onClick={handleBack}
            className="px-3 py-1 rounded bg-gray-500 text-white hover:bg-gray-600"
          >
            Back
          </button>
          <button
            onClick={handleDone}
            className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            Done
          </button>
          <button
            onClick={handleReject}
            className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
          >
            Reject
          </button>
        </div>
      </div>

      {/* --- Form Fields --- */}
      <div className="grid grid-cols-5 gap-4 mb-4">
        <div>
          <label className="block text-sm text-gray-600">Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            className="w-full border-b focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600">Patient ID</label>
          <input
            type="text"
            value={formData.patientId}
            onChange={(e) => handleInputChange("patientId", e.target.value)}
            className="w-full border-b focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600">Age</label>
          <input
            type="number"
            value={formData.age}
            onChange={(e) => handleInputChange("age", e.target.value)}
            className="w-full border-b focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600">Test date</label>
          <input
            type="date"
            value={formData.testDate ? formatToISODate(formData.testDate) : ""}
            onChange={(e) =>
              handleInputChange("testDate", formatToDDMMYYYYFromISO(e.target.value))
            }
            className="w-full border-b focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600">Report date</label>
          <input
            type="date"
            value={formData.reportDate ? formatToISODate(formData.reportDate) : ""}
            onChange={(e) =>
              handleInputChange("reportDate", formatToDDMMYYYYFromISO(e.target.value))
            }
            className="w-full border-b focus:outline-none"
          />
        </div>
      </div>

      {/* --- Gender Radio Buttons --- */}
      <div className="flex items-center gap-6">
        <label className="text-sm text-gray-600">Gender</label>
        {["Male", "Female", "Others"].map((g) => (
          <label key={g} className="flex items-center gap-2">
            <input
              type="radio"
              name="gender"
              value={g}
              checked={formData.gender === g}
              onChange={(e) => handleInputChange("gender", e.target.value)}
            />
            {g}
          </label>
        ))}
      </div>
    </div>
  );
}
